import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Settings as SettingsIcon, Building2, DatabaseBackup, UserPlus, ShieldCheck } from "lucide-react";

const ROLE_LABEL = { admin: "Beheerder", boekhouder: "Boekhouder", inspecteur: "Inspecteur (alleen-lezen)" };

export default function Settings() {
  const { user, isAdmin } = useAuth();
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "boekhouder" });
  const [saving, setSaving] = useState(false);

  const load = () => { if (isAdmin) api.get("/users").then((r) => setUsers(r.data)); };
  useEffect(() => { load(); }, [isAdmin]);

  const submit = async () => {
    setSaving(true);
    try {
      await api.post("/auth/register", form);
      toast.success("Gebruiker aangemaakt");
      setOpen(false); setForm({ name: "", email: "", password: "", role: "boekhouder" });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div>
        <h1 className="font-heading text-3xl font-semibold tracking-tight flex items-center gap-2">
          <SettingsIcon className="h-7 w-7 text-primary" /> Instellingen
        </h1>
        <p className="text-sm text-muted-foreground mt-1">Onderneming, gebruikers en systeeminformatie</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="shadow-sm">
          <CardHeader><CardTitle className="font-heading text-lg flex items-center gap-2"><Building2 className="h-5 w-5" /> Onderneming</CardTitle></CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-muted-foreground">Naam</span><span className="font-medium">Singh Express Shop BV</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Ondernemingsnr.</span><span className="font-mono">BE 0788.952.072</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Regeling</span><span>Dagboek van ontvangsten</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Circulaire</span><span>2020/C/20 (FOD Financiën)</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">BTW-tarieven</span><span>0% · 6% · 21%</span></div>
          </CardContent>
        </Card>

        <Card className="shadow-sm">
          <CardHeader><CardTitle className="font-heading text-lg flex items-center gap-2"><DatabaseBackup className="h-5 w-5" /> Bewaring & back-up</CardTitle></CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-emerald-600" /> Boekingen onveranderlijk na registratie</div>
            <div className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-emerald-600" /> Cryptografische audit trail (SHA-256 keten)</div>
            <div className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-emerald-600" /> Documenten bewaard: 10 jaar</div>
            <div className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-emerald-600" /> Volledige online toegang voor inspectie</div>
            <p className="text-xs text-muted-foreground pt-2">Gegevens worden opgeslagen in MongoDB met dagelijkse back-up op infrastructuurniveau.</p>
          </CardContent>
        </Card>
      </div>

      {isAdmin && (
        <Card className="shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="font-heading text-lg">Gebruikers & rollen</CardTitle>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild><Button size="sm" data-testid="open-user"><UserPlus className="h-4 w-4 mr-2" /> Gebruiker toevoegen</Button></DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle className="font-heading">Nieuwe gebruiker</DialogTitle><DialogDescription>Maak een account aan en ken een rol toe (beheerder, boekhouder of inspecteur).</DialogDescription></DialogHeader>
                <div className="space-y-4">
                  <div className="space-y-1.5"><Label>Naam</Label><Input data-testid="user-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
                  <div className="space-y-1.5"><Label>E-mail</Label><Input type="email" data-testid="user-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
                  <div className="space-y-1.5"><Label>Wachtwoord</Label><Input type="password" data-testid="user-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></div>
                  <div className="space-y-1.5">
                    <Label>Rol</Label>
                    <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                      <SelectTrigger data-testid="user-role"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="boekhouder">Boekhouder</SelectItem>
                        <SelectItem value="inspecteur">Inspecteur (alleen-lezen)</SelectItem>
                        <SelectItem value="admin">Beheerder</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <DialogFooter><Button onClick={submit} disabled={saving} data-testid="submit-user">{saving ? "Bezig…" : "Aanmaken"}</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader><TableRow><TableHead>Naam</TableHead><TableHead>E-mail</TableHead><TableHead>Rol</TableHead></TableRow></TableHeader>
              <TableBody>
                {users.map((u) => (
                  <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                    <TableCell className="font-medium">{u.name}</TableCell>
                    <TableCell className="text-sm">{u.email}</TableCell>
                    <TableCell><Badge variant="outline">{ROLE_LABEL[u.role] || u.role}</Badge></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <Card className="shadow-sm">
        <CardHeader><CardTitle className="font-heading text-lg">Uw account</CardTitle></CardHeader>
        <CardContent className="text-sm space-y-1">
          <div className="flex justify-between"><span className="text-muted-foreground">Naam</span><span className="font-medium">{user?.name}</span></div>
          <div className="flex justify-between"><span className="text-muted-foreground">E-mail</span><span>{user?.email}</span></div>
          <div className="flex justify-between"><span className="text-muted-foreground">Rol</span><span>{ROLE_LABEL[user?.role]}</span></div>
        </CardContent>
      </Card>
    </div>
  );
}
